<script setup>
// RM-359 Part B: "Ask RM for feedback" before submitting. The request (as it
// is now) goes to RM member support; RM answers in RM AI and by text or email.
// Nothing is submitted and the draft is kept as it is.
import { ref } from 'vue'
import { useReservationsStore } from '@/stores/reservations.store'

const props = defineProps({
  reservationId: { type: String, required: true },
  // The request screens save the draft first so RM reads it as it stands; the
  // chat has no open request to save (RM AI saved it as it went).
  saveFirst: { type: Boolean, default: true },
  // RM-359 (Kapil 10/30): opened from a link instead ("ask RM" in the spend guidance): no button of its own.
  hideButton: { type: Boolean, default: false },
})

const reservationsStore = useReservationsStore()
const isOpen = ref(false)
const question = ref('')
const sending = ref(false)
const sentMessage = ref('')
const errorMessage = ref('')

const openDialog = (preset = '') => {
  sentMessage.value = ''
  errorMessage.value = ''
  if (typeof preset === 'string' && preset) question.value = preset
  isOpen.value = true
}

// "ask RM" in the spend guidance opens it with the question already in it.
defineExpose({ open: openDialog })

const send = async () => {
  sending.value = true
  errorMessage.value = ''
  try {
    // RM reads the request as it stands now.
    if (props.saveFirst) await reservationsStore.saveEditReservation(true)
    sentMessage.value = await reservationsStore.askRmForFeedback(props.reservationId, question.value)
    question.value = ''
  } catch (error) {
    errorMessage.value = error?.response?.data?.error || 'Couldn\'t reach RM just now. Try again'
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <div class="ask-rm">
    <!-- Kapil 10/27: it has to read as a button, not a line of text -->
    <VBtn
      v-if="!hideButton"
      variant="outlined"
      color="primary-dark"
      rounded="pill"
      block
      prepend-icon="mdi-message-text-outline"
      class="ask-rm-btn"
      @click="openDialog()"
    >
      Ask RM for feedback first
    </VBtn>
    <VDialog
      v-model="isOpen"
      max-width="440"
    >
      <VCard class="ask-rm-card">
        <VCardText>
          <h3 class="ask-rm-title">
            Ask RM for feedback
          </h3>
          <template v-if="sentMessage">
            <p class="ask-rm-body">
              {{ sentMessage }}
            </p>
            <VBtn
              block
              color="primary-dark"
              class="mt-4"
              @click="isOpen = false"
            >
              Done
            </VBtn>
          </template>
          <template v-else>
            <p class="ask-rm-body">
              RM's team looks at this request before you submit it and replies here in RM AI. Nothing is submitted.
            </p>
            <VTextarea
              v-model="question"
              class="ask-rm-input mt-3"
              label="What would you like RM's view on? (optional)"
              rows="2"
              auto-grow
              maxlength="500"
            />
            <p
              v-if="errorMessage"
              class="text-error ask-rm-error"
            >
              {{ errorMessage }}
            </p>
            <VBtn
              block
              color="primary-dark"
              class="mt-2"
              :loading="sending"
              @click="send"
            >
              Send to RM
            </VBtn>
            <VBtn
              block
              variant="text"
              class="mt-1"
              :disabled="sending"
              @click="isOpen = false"
            >
              Cancel
            </VBtn>
          </template>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.ask-rm-btn {
  min-height: 44px;
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.ask-rm-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
}

.ask-rm-body {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
}

/* iOS zooms into inputs under 16px */
.ask-rm-input :deep(textarea) {
  font-size: 16px;
}

.ask-rm-error {
  margin: 4px 0 0;
  font-size: 14px;
}
</style>
